/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import memoize from 'lodash/memoize';
import { Box, xcss, Text } from '@atlaskit/primitives';
import InformationIcon from '@atlaskit/icon/core/information';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { HoverPopover, CellHoverRenderer } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

/**
 * We memoize this event by the **first** argument, e.g. fieldType, as we only really care about the proportion of
 * users encountering this scenario. We don't need to send an event each time this component is rendered.
 */
export const fireTrackAnalyticsOncePerFieldType = memoize(
	(fieldType: string, numberOfElements: number, createAnalyticsEvent) => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'tableCell multipleElementsRendered', {
			fieldType,
			numberOfElements,
		});
	},
);

type Props = {
	/**
	 * Table cell element to render for an issue field.
	 */
	cellElements: ReactNode[];
	/**
	 * Flag to enable min-height containers around each individual cell element as a stop-gap solution to fix layout
	 * issues with duplicate inline editable fields (which have built-in `min-height: 100%`).
	 * TODO JSC-1783 Handle duplicate field layouts within single row height
	 */
	multipleFieldLayoutAdjustment?: boolean;
	/**
	 * Type of the issue field.
	 */
	type: string;
};

const MoreFieldsContent = ({ elements }: { elements: ReactNode[] }) => {
	const { formatMessage } = useIntl();
	return (
		<Box>
			{/* Row */}
			<Box xcss={moreFieldsHeaderRowStyles}>
				<InformationIcon color={token('color.icon.information')} label="" />

				<Box xcss={moreFieldsHeaderStyles}>
					<Text size="medium" weight="medium">
						{formatMessage(messages.duplicateFieldsHeader)}
					</Text>
				</Box>
			</Box>
			{/* Body */}
			<Box xcss={moreFieldsDescriptionStyles}>{formatMessage(messages.duplicateFieldsContent)}</Box>
			<hr role="presentation" />
			<Box xcss={fieldsContainerStyles}>{elements.map((element) => element)}</Box>
		</Box>
	);
};

export const MultipleCellElements = ({
	cellElements,
	multipleFieldLayoutAdjustment = false,
	type,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	fireTrackAnalyticsOncePerFieldType(type, cellElements.length, createAnalyticsEvent);
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	if (isSingleLineRowHeightEnabled && cellElements.length > 0) {
		const firstElement = cellElements[0];
		const rest = cellElements.slice(1);

		return (
			<Box xcss={rowStyle}>
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<div css={[contentStyles, fillHeight, focusRingStyles]} {...props}>
								{firstElement}
							</div>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
				<CellHoverProvider>
					<CellHoverRenderer>
						{(props) => (
							<HoverPopover<HTMLButtonElement>
								content={<MoreFieldsContent elements={rest} />}
								isInteractive
							>
								{({ ref }) => (
									<IconButton
										label=""
										appearance="subtle"
										ref={ref}
										icon={(iconProps) => (
											<InformationIcon {...iconProps} color={token('color.icon.information')} />
										)}
										testId="native-issue-table.ui.row.issue-row.cell.multiple-cell-elements.issue-table"
										{...props}
									/>
								)}
							</HoverPopover>
						)}
					</CellHoverRenderer>
				</CellHoverProvider>
			</Box>
		);
	}

	return multipleFieldLayoutAdjustment ? (
		<Box xcss={containerStyles}>
			{cellElements.map((cellElement, index) => (
				<Box key={index} xcss={elementContainerStyles}>
					{cellElement}
				</Box>
			))}
		</Box>
	) : (
		cellElements
	);
};

const rowStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 'space.050',
	height: '100%',
});

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	gap: 'space.050',
});

const elementContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: '32px',
});

const moreFieldsHeaderRowStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
	marginBottom: 'space.050',
});

const moreFieldsHeaderStyles = xcss({
	marginLeft: 'space.100',
});

const moreFieldsDescriptionStyles = xcss({
	font: 'font.body.medium',
});

const fieldsContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	gap: 'space.050',
	width: '100%',
});

const contentStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const fillHeight = css({
	height: '100%',
});

const focusRingStyles = css({
	'&:focus-within': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});
